import "./ctl-tab.js";
import { dispatchEvent } from '../../../../utils/events.js';

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display:block;
    box-sizing: border-box;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    color: var(--ctl-color-text);
  }

  .tabs{
    display:flex;
    gap: var(--ctl-space-2);
    align-items:center;
    flex-wrap:wrap;
  }

  /* Underline variant: just a bottom border and indicator-ish styling on tabs */
  :host([variant="underline"]) .tabs{
    border-bottom: var(--ctl-border-width-thin, 1px) solid var(--ctl-color-border, #e2e8f0);
    padding-bottom: var(--ctl-space-2, 8px);
  }

  /* Pill variant: container pill background */
  :host([variant="pill"]) .tabs{
    background: var(--ctl-color-surface);
    background: color-mix(in srgb, var(--ctl-color-border) 20%, transparent);
    padding: var(--ctl-space-2);
    border-radius: var(--ctl-radius-md);
  }

  .panels{
    padding-top: var(--ctl-space-3);
  }

  /* hide helper slot templates */
  ::slotted(template[slot="tab"]){ display:none; }

  /* Hide ctl-tab elements while they're pending slot assignment to panels slot */
  #panelChildSlot::slotted(ctl-tab){ display:none; }
</style>

<div class="tabs" id="tablist" role="tablist" part="tablist">
  <slot name="tab" id="tabSlot"></slot>
  <slot name="tab-item" id="tabChildSlot"></slot>
</div>

<div class="panels" part="panels">
  <slot name="panel" id="panelSlot"></slot>
  <slot id="panelChildSlot"></slot>
</div>
`;

function uid(prefix = "ctl") {
  return `${prefix}-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
}

function isEl(n){ return n && n.nodeType === 1; }

export class CtlTabs extends HTMLElement {
  static get observedAttributes() {
    return ["value", "variant", "lazy"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._tablist = this.shadowRoot.getElementById("tablist");
    this._tabSlot = this.shadowRoot.getElementById("tabSlot");
    this._tabChildSlot = this.shadowRoot.getElementById("tabChildSlot");
    this._panelSlot = this.shadowRoot.getElementById("panelSlot");
    this._panelChildSlot = this.shadowRoot.getElementById("panelChildSlot");

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onTabClick = this._onTabClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    this._internalValue = "";
    this._activated = new Set(); // for lazy panels
  }

  connectedCallback(){
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "underline");

    this._tabSlot.addEventListener("slotchange", this._onSlotChange);
    this._tabChildSlot.addEventListener("slotchange", this._onSlotChange);
    this._panelSlot.addEventListener("slotchange", this._onSlotChange);
    this._panelChildSlot.addEventListener("slotchange", this._onSlotChange);

    this._tablist.addEventListener("click", this._onTabClick);
    this._tablist.addEventListener("keydown", this._onKeyDown);

    queueMicrotask(() => {
      this._autoAssignSlots();
      this._sync();
    });
  }

  disconnectedCallback(){
    this._tabSlot.removeEventListener("slotchange", this._onSlotChange);
    this._tabChildSlot.removeEventListener("slotchange", this._onSlotChange);
    this._panelSlot.removeEventListener("slotchange", this._onSlotChange);
    this._panelChildSlot.removeEventListener("slotchange", this._onSlotChange);

    this._tablist.removeEventListener("click", this._onTabClick);
    this._tablist.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback(){
    this._sync();
  }

  // --- API ---
  get value(){
    // controlled if attribute set, else internal
    return this.hasAttribute("value") ? (this.getAttribute("value") || "") : (this._internalValue || "");
  }
  set value(v){
    const nv = String(v ?? "");
    // If user sets property, treat as controlled by setting attribute
    this.setAttribute("value", nv);
  }

  get variant(){ return this.getAttribute("variant") || "underline"; }
  set variant(v){ this.setAttribute("variant", String(v || "underline")); }

  get lazy(){ return this.hasAttribute("lazy"); }
  set lazy(v){ v ? this.setAttribute("lazy","") : this.removeAttribute("lazy"); }

  // --- internals ---
  _onSlotChange(){
    this._autoAssignSlots();
    this._sync();
  }

  _autoAssignSlots(){
    // Auto-assign ctl-tab children to the named "tab-item" slot so they render
    // inside the tablist div. ctl-tab-panel children have no slot attribute and
    // naturally fall into the unnamed default slot (panels area).
    //
    // Contract: only elements without an existing slot attribute are assigned,
    // so consumers using explicit slot="tab" for advanced cases are not affected.
    // If a slot attribute is later removed from a ctl-tab, the next slotchange
    // event will re-assign it to "tab-item" — this is intentional.
    for (const child of this.children) {
      if (child.tagName === "CTL-TAB" && !child.hasAttribute("slot")) {
        child.setAttribute("slot", "tab-item");
      }
    }
  }

  _tabs(){
    // Prefer <ctl-tab> children (default slot), but also allow templates in slot="tab"
    const fromChildren = this._tabChildSlot.assignedElements({ flatten: true }).filter(el => el.tagName === "CTL-TAB");
    const fromTabSlot = this._tabSlot.assignedElements({ flatten: true }).filter(el => el.tagName === "CTL-TAB");
    const tabs = [...fromTabSlot, ...fromChildren];

    return tabs.filter(isEl);
  }

  _panels(){
    const fromChildren = this._panelChildSlot.assignedElements({ flatten: true }).filter(el => el.tagName === "CTL-TAB-PANEL");
    const fromPanelSlot = this._panelSlot.assignedElements({ flatten: true }).filter(el => el.tagName === "CTL-TAB-PANEL");
    const panels = [...fromPanelSlot, ...fromChildren];

    return panels.filter(isEl);
  }

  _ensureValue(){
    const tabs = this._tabs();
    if (!tabs.length) return "";

    // prefer current value if exists among tabs
    const current = this.value;
    if (current && tabs.some(t => t.value === current)) return current;

    // else first tab value, or generate
    const first = tabs[0];
    if (!first.value) first.value = "tab-1";
    const v = first.value;

    if (this.hasAttribute("value")) {
      // keep controlled in sync
      this.setAttribute("value", v);
    } else {
      this._internalValue = v;
    }
    return v;
  }

  _sync(){
    const tabs = this._tabs();
    const panels = this._panels();

    const activeValue = this._ensureValue();

    // Map tab value -> panel, by matching panel.value (recommended)
    const panelByValue = new Map();
    for (const p of panels) {
      if (!p.value) {
        // attempt to inherit value from attribute, else generate
        p.value = p.getAttribute("value") || "";
      }
      if (p.value) panelByValue.set(p.value, p);
    }

    // Ensure ids for aria
    for (const t of tabs) {
      if (!t.id) t.id = uid("ctl-tab");
    }
    for (const p of panels) {
      if (!p.id) p.id = uid("ctl-panel");
    }

    // Wire a11y + selection
    tabs.forEach((t, i) => {
      if (!t.value) t.value = `tab-${i+1}`;
      const selected = (t.value === activeValue);

      const panel = panelByValue.get(t.value) || null;

      t.setAttribute("role", "tab");
      t.setAttribute("aria-selected", selected ? "true" : "false");
      t.tabIndex = selected ? 0 : -1;
      t.variant = this.variant;

      // Connect tab -> panel
      if (panel) {
        t.setAttribute("aria-controls", panel.id);
        panel.setAttribute("aria-labelledby", t.id);
      } else {
        t.removeAttribute("aria-controls");
      }
    });

    // Panels show/hide (and lazy)
    panels.forEach((p) => {
      p.setAttribute("role", "tabpanel");
      const isActive = (p.value === activeValue);

      if (isActive) this._activated.add(p.value);

      const shouldRender = !this.lazy || this._activated.has(p.value);

      p.hidden = !isActive;

      // lazy behavior: ctl-tab-panel owns its own placeholdering
      p._setLazy?.(this.lazy, shouldRender);
    });

    // reflect host variant attr normalization
    if (!["underline", "pill"].includes(this.variant)) this.setAttribute("variant", "underline");
  }

  _setActive(nextValue, { emit = true } = {}){
    const tabs = this._tabs();
    if (!tabs.some(t => t.value === nextValue)) return;

    if (this.hasAttribute("value")) {
      this.setAttribute("value", nextValue);
    } else {
      this._internalValue = nextValue;
      this._sync();
    }

    if (emit) {
      dispatchEvent(this, "ctl-change", { value: nextValue });
    }
  }

  _onTabClick(e){
    const path = e.composedPath ? e.composedPath() : [];
    const tab = path.find(n => n && n.tagName === "CTL-TAB");
    if (!tab) return;
    if (tab.disabled) return;

    this._setActive(tab.value, { emit: true });
    tab.focus?.();
  }

  _enabledTabs(){
    return this._tabs().filter(t => !t.disabled);
  }

  _moveFocus(delta){
    const tabs = this._enabledTabs();
    if (!tabs.length) return;

    const activeEl = this.getRootNode().activeElement;
    let idx = tabs.indexOf(activeEl);
    if (idx < 0) idx = tabs.findIndex(t => t.tabIndex === 0);
    if (idx < 0) idx = 0;

    const next = tabs[(idx + delta + tabs.length) % tabs.length];
    tabs.forEach(t => t.tabIndex = -1);
    next.tabIndex = 0;
    next.focus?.();
  }

  _onKeyDown(e){
    // roving focus on tablist
    if (e.key === "ArrowRight") { e.preventDefault(); this._moveFocus(+1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); this._moveFocus(-1); }
    else if (e.key === "Home") {
      e.preventDefault();
      const tabs = this._enabledTabs();
      if (tabs[0]) { tabs.forEach(t => t.tabIndex = -1); tabs[0].tabIndex = 0; tabs[0].focus?.(); }
    }
    else if (e.key === "End") {
      e.preventDefault();
      const tabs = this._enabledTabs();
      const last = tabs[tabs.length - 1];
      if (last) { tabs.forEach(t => t.tabIndex = -1); last.tabIndex = 0; last.focus?.(); }
    }
    else if (e.key === "Enter" || e.key === " ") {
      const activeEl = this.getRootNode().activeElement;
      if (activeEl && activeEl.tagName === "CTL-TAB" && !activeEl.disabled) {
        e.preventDefault();
        this._setActive(activeEl.value, { emit: true });
      }
    }
  }
}

customElements.define("ctl-tabs", CtlTabs);
